import { useMemo, useState } from "react";
import type { IntervieweeCard, IntervieweeDetail, MediaPreview, VillageViewModel } from "../../../domain/contracts";
import { buildDisplacementReasonSummary } from "../../../services/displacementReasons";
import { buildEvidenceGroupItemLabel, buildEvidenceGroupTitle } from "../../../services/evidenceLabels";
import { toDownloadEvidenceItem } from "../../../services/evidenceGroups";
import { useIntervieweeIncidentViewModel } from "../../incidents/hooks/useIntervieweeIncidentViewModel";
import type { IntervieweeMediaOverlayItem } from "../types";
import { useEvidenceDownloadAll } from "./useEvidenceDownloadAll";
import { useIntervieweeEvidence } from "./useIntervieweeEvidence";
import { useIntervieweeProfileMeta } from "./useIntervieweeProfileMeta";

export type IntervieweeSectionViewModelParams = {
  village: VillageViewModel | null;
  selectedInterviewee: IntervieweeCard | null;
  intervieweeDetail: IntervieweeDetail | null;
  mediaPreviews: MediaPreview[];
  returnTo: "map" | "villagesList" | "intervieweesList" | "village";
};

export type IntervieweeEvidenceDisplayItem = {
  key: string;
  previewLabel: string;
  previewSource: string;
  mediaLine: string;
  overlayItem: IntervieweeMediaOverlayItem | null;
};

export type IntervieweeEvidenceDisplayGroup = {
  key: string;
  title: string;
  items: IntervieweeEvidenceDisplayItem[];
};

export type IntervieweeSectionViewModel = {
  witnessHeading: string;
  backLabel: string;
  documentEvidenceLabel: string;
  displacementReasonLabel: string;
  violationItems: ReturnType<typeof useIntervieweeProfileMeta>["violationItems"];
  activeViolationText: ReturnType<typeof useIntervieweeProfileMeta>["activeViolationText"];
  setActiveViolation: ReturnType<typeof useIntervieweeProfileMeta>["setActiveViolation"];
  timelineItems: ReturnType<typeof useIntervieweeProfileMeta>["timelineItems"];
  hasIncidentDetails: boolean;
  witnessItems: ReturnType<typeof useIntervieweeIncidentViewModel>["witnessItems"];
  incidentCards: ReturnType<typeof useIntervieweeIncidentViewModel>["incidentCards"];
  evidenceGroups: IntervieweeEvidenceDisplayGroup[];
  isDownloadingAll: boolean;
  handleDownloadAll: () => void;
  hasDownloadItems: boolean;
  activePreview: IntervieweeMediaOverlayItem | null;
  openPreview: (item: IntervieweeMediaOverlayItem | null) => void;
  closePreview: () => void;
};

export function useIntervieweeSectionViewModel({
  village,
  selectedInterviewee,
  intervieweeDetail,
  mediaPreviews,
  returnTo,
}: IntervieweeSectionViewModelParams): IntervieweeSectionViewModel {
  const hasPreviewMatch = Boolean(village?.backendRef);
  const witnessHeading = selectedInterviewee?.publicPseudonym ?? (hasPreviewMatch ? "Select an interviewee" : "No linked witness yet");
  const villageName = village?.title ?? "Village";

  const [activePreview, setActivePreview] = useState<IntervieweeMediaOverlayItem | null>(null);

  const {
    violationItems,
    activeViolationText,
    setActiveViolation,
    timelineItems,
  } = useIntervieweeProfileMeta({ selectedInterviewee, intervieweeDetail });

  const {
    hasIncidentDetails,
    witnessItems,
    incidentCards,
  } = useIntervieweeIncidentViewModel(intervieweeDetail?.profile ?? null);

  const { evidenceGroups, previewByAssetId, downloadItems } = useIntervieweeEvidence(intervieweeDetail, mediaPreviews);
  const { isDownloading: isDownloadingAll, handleDownloadAll } = useEvidenceDownloadAll(downloadItems);

  const backLabel = returnTo === "intervieweesList"
    ? "Back to Survivors"
    : `Back to ${villageName} Village`;

  const documentEvidence = intervieweeDetail?.documentEvidence ?? [];
  const documentEvidenceLabel = documentEvidence.length > 0 ? documentEvidence.join(", ") : "-";
  const displacementReasonLabel = buildDisplacementReasonSummary(intervieweeDetail?.profile?.displacement ?? null);

  const evidenceDisplayGroups = useMemo<IntervieweeEvidenceDisplayGroup[]>(() => (
    evidenceGroups.map((group) => {
      const groupTitle = buildEvidenceGroupTitle(group.groupLabel, group.itemIndex);

      return {
        key: `${group.branchKind}:${group.itemIndex}`,
        title: groupTitle,
        items: group.items.map((item) => {
          const preview = previewByAssetId.get(item.assetId) ?? null;
          const previewLabel = buildEvidenceGroupItemLabel({
            category: group.groupLabel,
            captionDefault: item.captionDefault,
            groupLabel: group.groupLabel,
            slotLabel: item.slotLabel,
            itemIndex: item.itemIndex,
            detailIndex: item.detailIndex,
          });
          const placeholder = preview?.placeholder ?? "/document-icon.png";
          const previewSource = preview?.objectUrl ?? placeholder;
          const evidenceItem = toDownloadEvidenceItem(group, item);

          return {
            key: `${item.assetId}:${item.slotType}:${item.detailIndex ?? 0}`,
            previewLabel,
            previewSource,
            mediaLine: `${item.mediaType} ${item.redactionStatus}`,
            overlayItem: preview?.objectUrl ? { evidence: evidenceItem, preview } : null,
          };
        }),
      };
    })
  ), [evidenceGroups, previewByAssetId]);

  function openPreview(item: IntervieweeMediaOverlayItem | null) {
    if (!item?.preview?.objectUrl) {
      return;
    }

    setActivePreview(item);
  }

  function closePreview() {
    setActivePreview(null);
  }

  return {
    witnessHeading,
    backLabel,
    documentEvidenceLabel,
    displacementReasonLabel,
    violationItems,
    activeViolationText,
    setActiveViolation,
    timelineItems,
    hasIncidentDetails,
    witnessItems,
    incidentCards,
    evidenceGroups: evidenceDisplayGroups,
    isDownloadingAll,
    handleDownloadAll,
    hasDownloadItems: downloadItems.length > 0,
    activePreview,
    openPreview,
    closePreview,
  };
}
